import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SchemaPreview {
  tableName: string;
  fields: Array<{
    name: string;
    type: string;
    isRequired?: boolean;
    config?: { options?: string[] };
  }>;
  records: Array<Record<string, any>>;
  confidence: number;
}

export function useSchemaDetection() {
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedSchema, setDetectedSchema] = useState<SchemaPreview | null>(null);

  const detectSchema = async (content: string, context?: string) => {
    if (!content.trim()) {
      toast.error("Please provide content to analyze");
      return null;
    }

    setIsDetecting(true);

    try {
      const { data, error } = await supabase.functions.invoke("detect-schema", {
        body: { content, context }
      });

      if (error) throw error;

      if (data.error) {
        if (data.error.includes("Rate limit")) {
          toast.error("AI rate limit reached. Please try again later.");
        } else if (data.error.includes("credits")) {
          toast.error("AI credits exhausted. Please add credits in Settings.");
        } else {
          toast.error(data.error);
        }
        return null;
      }

      setDetectedSchema(data);
      
      toast.success("Structure detected!", {
        description: `Found ${data.fields.length} fields with ${Math.round(data.confidence * 100)}% confidence`
      });

      return data;
    } catch (error: any) {
      console.error("Error detecting schema:", error);
      toast.error("Failed to detect structure. Please try again.");
      return null;
    } finally {
      setIsDetecting(false);
    }
  };

  const clearSchema = () => {
    setDetectedSchema(null);
  };

  return {
    isDetecting,
    detectedSchema,
    detectSchema,
    clearSchema
  };
}
